// hooks/useTagQuerySync.ts
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addTag, markPathInitialized } from '../Component/stores/tags-view.store';
import { AppState } from '../Component/stores';

/**
 * Hook for syncing Redux tag query state with localStorage and restoring it.
 *
 * @param label - Tab label in both languages
 * @param code - Unique tab code (e.g., 'account')
 * @param queryParams - Current query param values (should be state)
 * @param setFunctions - Functions to restore query params
 */
export function useTagQuerySync<T extends Record<string, any>>(
  label: { en_US: string; zh_CN: string },
  code: string,
  queryParams: T,
  setFunctions: Partial<{ [K in keyof T]: (val: T[K]) => void }>,
) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { tags } = useSelector((state: AppState) => state.tagsView);

  const path = location.pathname;

  // 1. Восстановление из localStorage, если path ещё не был инициализирован
  useEffect(() => {
    const tag = tags.find((t) => t.path === path);

    if (tag?.queryParams) {
      Object.entries(tag.queryParams).forEach(([key, value]) => {
        const setter = setFunctions[key as keyof T];
        if (setter) setter(value);
      });
    }

    // 2. Помечаем как инициализированный (важно: только 1 раз)
    dispatch(markPathInitialized(path));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Обновление состояния вкладки при изменении queryParams
  useEffect(() => {
    // Фильтрация: исключить любые функции из queryParams
    const serializableParams = Object.fromEntries(
      Object.entries(queryParams).filter(([_, value]) => typeof value !== 'function'),
    );

    dispatch(
      addTag({
        path,
        label,
        closable: true,
        queryParams: serializableParams, // ⬅️ Только сериализуемые значения
        code,
      }),
    );
  }, [dispatch, path, code, label.en_US, label.zh_CN, JSON.stringify(queryParams)]);
}
